import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQuery } from "convex/react";
import {
  ArrowLeft,
  BadgeCheck,
  Brain,
  Clock,
  ImageOff,
  Minus,
  Package,
  Plus,
  Repeat,
  Search,
  Star,
  UtensilsCrossed,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { SourceChip } from "../components/SourceChip";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { useLoggingFlow } from "../lib/logging-flow";
import { normalizeOffSearchTerm, OFF_ATTRIBUTION, searchOffProducts } from "../lib/openfoodfacts-client";
import { usePlate } from "../lib/plate";
import { formatNumber } from "../lib/format";
import type {
  CustomFoodView,
  FavoriteView,
  FrequentFoodView,
  LibraryEntry,
  MemoryView,
  RecipeView,
} from "./library-utils";
import { kcalLabel } from "./library-utils";
import type {
  LocalSearchTabId,
  OffSearchState,
  OffProductView,
  SearchDataSets,
  SearchResultModel,
  SearchTabId,
  ServingSelection,
} from "./food-search-utils";
import {
  IDLE_OFF_SEARCH_STATE,
  OFF_SEARCH_ERROR_MESSAGE,
  SEARCH_TABS,
  defaultServingSelection,
  filterOffResults,
  gramsAvailable,
  macroPreviewLine,
  offProductToResult,
  offSearchReducer,
  resultToPlateItem,
  resultToReviewItem,
  resultsForTab,
  runOffSearch,
  selectionForMode,
  stepQuantity,
} from "./food-search-utils";

// Food Search screen (plan todo 17) — full-screen flow kind "search". Local
// sections (Recent / Frequent / Memories / Favourites / Custom foods /
// Recipes) filter INSTANTLY as the user types (server-backed memory/custom
// searches re-arm on a 300ms debounce); the Verified OpenFoodFacts section
// queries the OFF API browser-direct (per-user rate limit) on explicit
// submit AND on an 800ms debounce for 3+ character queries, deduped and
// aborted when superseded. Every result can be added to the plate or logged
// now via a prefilled review; unknown nutrition renders "—" and is never
// zero-filled.

export interface FoodSearchViewProps {
  readonly query: string;
  readonly onQueryChange: (value: string) => void;
  readonly onSubmitSearch: () => void;
  readonly activeTab: SearchTabId;
  readonly onTabChange: (tab: SearchTabId) => void;
  // Normalized results for the active LOCAL tab (undefined while loading).
  readonly results: readonly SearchResultModel[] | undefined;
  readonly offState: OffSearchState;
  readonly brandedOnly: boolean;
  readonly onBrandedOnlyChange: (value: boolean) => void;
  readonly onAddToPlate: (result: SearchResultModel, selection: ServingSelection) => void;
  readonly onLogNow: (result: SearchResultModel, selection: ServingSelection) => void;
  readonly onClose: () => void;
}

const LOCAL_EMPTY_COPY: Record<LocalSearchTabId, { icon: LucideIcon; title: string; message: string }> = {
  recent: {
    icon: Clock,
    title: "No recent foods",
    message: "Foods you log will show up here for fast re-logging.",
  },
  frequent: {
    icon: Repeat,
    title: "No frequent foods yet",
    message: "Log the same food a couple of times and it becomes a shortcut here.",
  },
  memories: {
    icon: Brain,
    title: "No food memories",
    message: "Memories are learned from your corrections and manual entries.",
  },
  favourites: {
    icon: Star,
    title: "No favourites yet",
    message: "Star foods, meals, and recipes in your library to pin them here.",
  },
  custom: {
    icon: Package,
    title: "No custom foods",
    message: "Scan a barcode or nutrition label to build your own food database.",
  },
  recipes: {
    icon: UtensilsCrossed,
    title: "No recipes yet",
    message: "Build a recipe in your library and log servings in one tap.",
  },
};

function ResultSkeletons({ testId }: { readonly testId: string }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-2" aria-busy="true">
      {[0, 1, 2].map((index) => (
        <div key={index} className="rounded-2xl border border-border bg-surface p-3">
          <div className="h-4 w-2/3 animate-pulse rounded bg-border" />
          <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-border" />
          <div className="mt-3 h-11 w-full animate-pulse rounded-xl bg-border" />
        </div>
      ))}
    </div>
  );
}

function unitButtonClass(active: boolean): string {
  return `min-h-[44px] px-3 text-xs font-semibold transition-all duration-200 ease-out active:scale-[0.98] select-none ${
    active ? "bg-accent-light text-accent-dark" : "bg-surface text-text-muted"
  }`;
}

interface SearchResultCardProps {
  readonly result: SearchResultModel;
  readonly onAddToPlate: (result: SearchResultModel, selection: ServingSelection) => void;
  readonly onLogNow: (result: SearchResultModel, selection: ServingSelection) => void;
}

function SearchResultCard({ result, onAddToPlate, onLogNow }: SearchResultCardProps) {
  const [selection, setSelection] = useState<ServingSelection>(() => defaultServingSelection(result));
  const [imageFailed, setImageFailed] = useState(false);
  const base = result.reviewItem;
  const scaled = resultToReviewItem(result, selection);
  const grams = gramsAvailable(result);
  const showScaledTotal = selection.mode === "grams" || selection.quantity !== result.defaultQuantity;
  const quantityUnit = selection.mode === "grams" ? "g" : result.servingLabel;
  const isOffResult = result.sourceRef?.kind === "off";
  const showCover = isOffResult && result.imageUrl !== undefined && !imageFailed;

  return (
    <article
      data-testid="food-search-result"
      className="rounded-2xl border border-border bg-surface p-3 shadow-sm"
    >
      {showCover && (
        <div
          data-testid="food-search-result-image"
          className="mb-2 flex h-44 w-full items-center justify-center overflow-hidden rounded-xl bg-muted p-2"
        >
          <img
            src={result.imageUrl}
            alt={result.name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      {isOffResult && !showCover && (
        <div
          data-testid="food-search-result-image-fallback"
          className="mb-2 flex h-16 w-full items-center justify-center gap-2 rounded-xl bg-muted text-text-muted"
        >
          <ImageOff size={18} aria-hidden="true" />
          <span className="text-xs">No product photo</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text">{result.name}</p>
          {result.subtitle !== undefined && (
            <p className="truncate text-xs text-text-muted">{result.subtitle}</p>
          )}
        </div>
        <SourceChip dataSource={result.dataSource} testId="food-search-result-source" />
      </div>

      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
        <span data-testid="food-search-result-kcal" className="text-sm font-semibold text-text">
          {kcalLabel(base.calories)}
        </span>
        <span className="text-xs text-text-muted">per {result.servingLabel}</span>
      </div>
      <p data-testid="food-search-result-macros" className="mt-0.5 text-xs text-text-muted">
        {macroPreviewLine(base)}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-xl border border-border bg-muted px-1 py-1">
          <button
            type="button"
            data-testid="food-search-qty-minus"
            aria-label={`Decrease quantity of ${result.name}`}
            onClick={() => setSelection((current) => stepQuantity(current, -1))}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-text transition-all duration-200 ease-out active:bg-surface active:scale-[0.98] select-none"
          >
            <Minus size={15} strokeWidth={2} aria-hidden="true" />
          </button>
          <span
            data-testid="food-search-result-qty"
            className="min-w-[72px] text-center text-sm font-medium text-text"
          >
            {formatNumber(selection.quantity)} {quantityUnit}
          </span>
          <button
            type="button"
            data-testid="food-search-qty-plus"
            aria-label={`Increase quantity of ${result.name}`}
            onClick={() => setSelection((current) => stepQuantity(current, 1))}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-text transition-all duration-200 ease-out active:bg-surface active:scale-[0.98] select-none"
          >
            <Plus size={15} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        {grams && (
          <div role="group" aria-label="Serving unit" className="flex overflow-hidden rounded-xl border border-border">
            <button
              type="button"
              data-testid="food-search-unit-servings"
              aria-pressed={selection.mode === "servings"}
              onClick={() => setSelection(selectionForMode(result, "servings"))}
              className={unitButtonClass(selection.mode === "servings")}
            >
              servings
            </button>
            <button
              type="button"
              data-testid="food-search-unit-grams"
              aria-pressed={selection.mode === "grams"}
              onClick={() => setSelection(selectionForMode(result, "grams"))}
              className={unitButtonClass(selection.mode === "grams")}
            >
              g
            </button>
          </div>
        )}
        {showScaledTotal && (
          <span data-testid="food-search-scaled-kcal" className="text-xs font-medium text-text-muted">
            = {kcalLabel(scaled.calories)}
          </span>
        )}
      </div>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="food-search-add-plate"
          onClick={() => onAddToPlate(result, selection)}
          className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-accent px-3 py-2 text-sm font-semibold text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
        >
          Add to plate
        </button>
        <button
          type="button"
          data-testid="food-search-log-now"
          onClick={() => onLogNow(result, selection)}
          className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-white transition-all duration-200 ease-out active:opacity-90 active:scale-[0.98] select-none"
        >
          Log now
        </button>
      </div>
    </article>
  );
}

interface LocalResultsProps {
  readonly tab: LocalSearchTabId;
  readonly results: readonly SearchResultModel[] | undefined;
  readonly query: string;
  readonly onAddToPlate: (result: SearchResultModel, selection: ServingSelection) => void;
  readonly onLogNow: (result: SearchResultModel, selection: ServingSelection) => void;
}

function LocalResults({ tab, results, query, onAddToPlate, onLogNow }: LocalResultsProps) {
  if (results === undefined) return <ResultSkeletons testId="food-search-loading" />;
  if (results.length === 0) {
    const trimmed = query.trim();
    const copy = LOCAL_EMPTY_COPY[tab];
    return (
      <div data-testid="food-search-empty">
        <EmptyState
          icon={copy.icon}
          title={trimmed.length > 0 ? `No matches for "${trimmed}"` : copy.title}
          message={trimmed.length > 0 ? "Try a different spelling, or search the Verified tab." : copy.message}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {results.map((result) => (
        <SearchResultCard
          key={result.key}
          result={result}
          onAddToPlate={onAddToPlate}
          onLogNow={onLogNow}
        />
      ))}
    </div>
  );
}

interface VerifiedSectionProps {
  readonly offState: OffSearchState;
  readonly brandedOnly: boolean;
  readonly onBrandedOnlyChange: (value: boolean) => void;
  readonly onRetry: () => void;
  readonly onAddToPlate: (result: SearchResultModel, selection: ServingSelection) => void;
  readonly onLogNow: (result: SearchResultModel, selection: ServingSelection) => void;
}

function VerifiedSection({
  offState,
  brandedOnly,
  onBrandedOnlyChange,
  onRetry,
  onAddToPlate,
  onLogNow,
}: VerifiedSectionProps) {
  switch (offState.status) {
    case "idle":
      return (
        <div data-testid="food-search-off-idle" className="rounded-2xl border border-border bg-surface p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-text">
            <BadgeCheck size={18} className="text-accent" aria-hidden="true" />
            Verified database
          </p>
          <p className="mt-1 text-sm text-text-muted">
            Results from OpenFoodFacts appear here once you type 3+ characters or press Search.
            Products with complete nutrition are marked &quot;Verified database&quot;; gaps are badged
            &quot;Incomplete nutrition&quot; so nothing is silently zero.
          </p>
        </div>
      );
    case "loading":
      return <ResultSkeletons testId="food-search-off-loading" />;
    case "error":
      return (
        <div
          data-testid="food-search-off-error"
          role="alert"
          className="rounded-2xl border border-danger/40 bg-surface p-4"
        >
          <p className="text-sm font-medium text-danger">{OFF_SEARCH_ERROR_MESSAGE}</p>
          <button
            type="button"
            data-testid="food-search-off-retry"
            onClick={onRetry}
            className="mt-2 flex min-h-[44px] items-center justify-center rounded-xl border border-accent px-4 py-2 text-sm font-semibold text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
          >
            Try again
          </button>
        </div>
      );
    case "done": {
      const filtered = filterOffResults(offState.results, brandedOnly);
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <p data-testid="food-search-off-count" className="text-xs text-text-muted">
              {offState.results.length} products for &quot;{offState.query}&quot;
            </p>
            <button
              type="button"
              data-testid="food-search-branded-filter"
              aria-pressed={brandedOnly}
              onClick={() => onBrandedOnlyChange(!brandedOnly)}
              className={unitButtonClass(brandedOnly)}
            >
              Branded foods
            </button>
          </div>
          {filtered.length === 0 ? (
            <div data-testid="food-search-empty">
              <EmptyState
                icon={BadgeCheck}
                title="No verified products"
                message={
                  brandedOnly
                    ? "No branded products matched. Turn off the branded filter to see everything."
                    : "The verified database had nothing for that search. Try different words."
                }
              />
            </div>
          ) : (
            filtered.map((product: OffProductView, index: number) => (
              <SearchResultCard
                key={product.barcode ?? `off-${index}`}
                result={offProductToResult(product, index)}
                onAddToPlate={onAddToPlate}
                onLogNow={onLogNow}
              />
            ))
          )}
          <p data-testid="food-search-off-attribution" className="px-1 text-[11px] text-text-muted">
            {OFF_ATTRIBUTION}
          </p>
        </div>
      );
    }
  }
}

export function FoodSearchView(props: FoodSearchViewProps) {
  const {
    query,
    onQueryChange,
    onSubmitSearch,
    activeTab,
    onTabChange,
    results,
    offState,
    brandedOnly,
    onBrandedOnlyChange,
    onAddToPlate,
    onLogNow,
    onClose,
  } = props;

  return (
    <div data-testid="food-search" className="fixed inset-0 z-40 flex flex-col bg-muted">
      <header className="w-full shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-xl items-center gap-2 px-4 py-3">
          <button
            type="button"
            data-testid="food-search-close"
            aria-label="Close food search"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <ArrowLeft size={20} strokeWidth={2} aria-hidden="true" />
          </button>
          <h2 className="text-lg font-semibold text-text">Search food</h2>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-8">
        <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 pt-4">
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitSearch();
            }}
          >
            <div className="flex-1">
              <Input
                data-testid="food-search-input"
                aria-label="Search foods"
                placeholder="Search foods..."
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
              />
            </div>
            <button
              type="submit"
              data-testid="food-search-submit"
              className="flex min-h-[44px] items-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-white transition-all duration-200 ease-out active:opacity-90 active:scale-[0.98] select-none"
            >
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              Search
            </button>
          </form>
          <p className="text-xs text-text-muted">
            Your sections filter as you type. The verified database searches after a short pause
            or when you press Search.
          </p>

          <div role="tablist" aria-label="Food search sections" className="grid grid-cols-4 gap-1.5">
            {SEARCH_TABS.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={tab.testId}
                  onClick={() => onTabChange(tab.id)}
                  className={`flex min-h-[44px] items-center justify-center rounded-xl border px-2 text-xs font-semibold transition-all duration-200 ease-out active:scale-[0.98] select-none ${
                    active
                      ? "border-accent bg-accent-light text-accent-dark"
                      : "border-border bg-surface text-text-muted"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <section data-testid="food-search-section" aria-label="Search results">
            {activeTab === "verified" ? (
              <VerifiedSection
                offState={offState}
                brandedOnly={brandedOnly}
                onBrandedOnlyChange={onBrandedOnlyChange}
                onRetry={onSubmitSearch}
                onAddToPlate={onAddToPlate}
                onLogNow={onLogNow}
              />
            ) : (
              <LocalResults
                tab={activeTab}
                results={results}
                query={query}
                onAddToPlate={onAddToPlate}
                onLogNow={onLogNow}
              />
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// ── Query/action-wired screen ───────────────────────────────────────────────

const MIN_OFF_QUERY_CHARS = 3;
const OFF_SEARCH_DEBOUNCE_MS = 800;

export interface FoodSearchProps {
  readonly onClose: () => void;
  // Pre-seed from the flow payload (payload.query — e.g. the barcode
  // not-found fallback carries the scanned code into the search field).
  readonly initialQuery?: string;
  // Test hooks (Plan initialRangeId precedent): SSR suites render specific
  // states without interaction.
  readonly initialTab?: SearchTabId;
  readonly initialOffState?: OffSearchState;
}

export default function FoodSearch({ onClose, initialQuery, initialTab, initialOffState }: FoodSearchProps) {
  const { startFlow } = useLoggingFlow();
  const { addItem } = usePlate();

  const [query, setQuery] = useState(initialQuery ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState((initialQuery ?? "").trim());
  const [activeTab, setActiveTab] = useState<SearchTabId>(initialTab ?? "recent");
  const [brandedOnly, setBrandedOnly] = useState(false);
  const [offState, dispatchOff] = useReducer(
    offSearchReducer,
    initialOffState ?? IDLE_OFF_SEARCH_STATE,
  );

  // 300ms debounce re-arms the server-backed memory/custom-food searches.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Only the ACTIVE tab subscribes; the rest stay "skip".
  const recentFoods = useQuery(
    api.meals.recentFoods,
    activeTab === "recent" ? { limit: 50 } : "skip",
  ) as LibraryEntry[] | undefined;
  const frequentFoods = useQuery(
    api.meals.frequentFoods,
    activeTab === "frequent" ? { limit: 50 } : "skip",
  ) as FrequentFoodView[] | undefined;
  const memoryList = useQuery(
    api.foodMemory.listAll,
    activeTab === "memories" && debouncedQuery === "" ? {} : "skip",
  ) as MemoryView[] | undefined;
  const memoryMatches = useQuery(
    api.foodMemory.search,
    activeTab === "memories" && debouncedQuery !== "" ? { query: debouncedQuery } : "skip",
  ) as MemoryView[] | undefined;
  const favourites = useQuery(
    api.savedMeals.listFavorites,
    activeTab === "favourites" ? {} : "skip",
  ) as FavoriteView[] | undefined;
  const customList = useQuery(
    api.customFoods.list,
    activeTab === "custom" && debouncedQuery === "" ? {} : "skip",
  ) as CustomFoodView[] | undefined;
  const customMatches = useQuery(
    api.customFoods.search,
    activeTab === "custom" && debouncedQuery !== "" ? { query: debouncedQuery } : "skip",
  ) as CustomFoodView[] | undefined;
  // NOTE: convex/recipes exposes list only (no search query) — the Recipes
  // section filters the list client-side like the other local sections.
  const recipes = useQuery(
    api.recipes.list,
    activeTab === "recipes" ? {} : "skip",
  ) as RecipeView[] | undefined;

  const dataSets: SearchDataSets = useMemo(
    () => ({
      recent: recentFoods,
      frequent: frequentFoods,
      memories: debouncedQuery === "" ? memoryList : memoryMatches,
      favourites,
      custom: debouncedQuery === "" ? customList : customMatches,
      recipes,
    }),
    [
      recentFoods,
      frequentFoods,
      memoryList,
      memoryMatches,
      favourites,
      customList,
      customMatches,
      recipes,
      debouncedQuery,
    ],
  );

  // Instant local filtering uses the IMMEDIATE query (not the debounced one).
  const results =
    activeTab === "verified" ? undefined : resultsForTab(activeTab, dataSets, query);

  const offAbortRef = useRef<AbortController | null>(null);
  const lastOffTermRef = useRef<string | null>(null);

  // Browser-direct OFF search: explicit submit AND 800ms debounce funnel
  // through here. Dedupe skips repeat terms; a new search aborts the
  // in-flight one, which resolves silently (no error flash).
  const triggerOffSearch = useCallback((rawQuery: string, switchToVerifiedTab: boolean) => {
    const term = normalizeOffSearchTerm(rawQuery);
    if (term.length < MIN_OFF_QUERY_CHARS) {
      console.warn("[Frontend][FoodSearch] Verified search skipped: query under 3 characters", {
        length: term.length,
      });
      return;
    }
    if (switchToVerifiedTab) setActiveTab("verified");
    if (lastOffTermRef.current === term) {
      console.debug("[Frontend][FoodSearch] Verified search deduped", { query: term });
      return;
    }
    lastOffTermRef.current = term;
    offAbortRef.current?.abort();
    const controller = new AbortController();
    offAbortRef.current = controller;
    console.log("[Frontend][FoodSearch] Submitting verified-database search", { query: term });
    void runOffSearch(
      (args) => searchOffProducts(args.query, 1, args.signal),
      term,
      dispatchOff,
      controller.signal,
    );
  }, []);

  // Debounced auto-search: fires silently without leaving the active tab.
  useEffect(() => {
    if (normalizeOffSearchTerm(query).length < MIN_OFF_QUERY_CHARS) return;
    const timer = setTimeout(() => triggerOffSearch(query, false), OFF_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, triggerOffSearch]);

  // Pending superseded searches never outlive the screen.
  useEffect(() => {
    return () => offAbortRef.current?.abort();
  }, []);

  const handleSubmitSearch = () => {
    if (query.trim().length === 0) {
      console.warn("[Frontend][FoodSearch] Submit ignored: empty query");
      return;
    }
    triggerOffSearch(query, true);
  };

  const handleAddToPlate = (result: SearchResultModel, selection: ServingSelection) => {
    const item = resultToPlateItem(result, selection);
    console.log("[Frontend][FoodSearch] Adding search result to plate", {
      name: item.name,
      dataSource: item.dataSource,
    });
    addItem(item);
  };

  const handleLogNow = (result: SearchResultModel, selection: ServingSelection) => {
    const item = resultToReviewItem(result, selection);
    console.log("[Frontend][FoodSearch] Log now: prefill review from search", {
      name: item.name,
      dataSource: item.dataSource,
    });
    startFlow("review", { items: [item], source: "search" });
  };

  return (
    <FoodSearchView
      query={query}
      onQueryChange={setQuery}
      onSubmitSearch={handleSubmitSearch}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      results={results}
      offState={offState}
      brandedOnly={brandedOnly}
      onBrandedOnlyChange={setBrandedOnly}
      onAddToPlate={handleAddToPlate}
      onLogNow={handleLogNow}
      onClose={onClose}
    />
  );
}
